import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';

function fileUrl(record, fileField, thumb) {
  try {
    if (!record || !record[fileField]) return '';
    return pb.files.getURL(record, record[fileField], thumb ? { thumb } : undefined);
  } catch {
    return '';
  }
}

const emptyForm = {
  key: '',
  title: '',
  content: '',
  is_active: true,
  sort_order: 0,
  image: null,
};

function AdminSiteHomeSectionsPage() {
  const [allowEmpty, setAllowEmpty] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const dragIndexRef = React.useRef(-1);

  const [sections, setSections] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);

  const [imageDragOver, setImageDragOver] = React.useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState('');
  const [removeExistingImage, setRemoveExistingImage] = React.useState(false);
  const imageInputRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      try {
        if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
      } catch {}
    };
  }, [imagePreviewUrl]);

  const fetchSections = async () => {
    setError('');
    try {
      const res = await pb.collection('site_home_sections').getList(1, 200, { sort: 'sort_order, -created' });
      setSections(res?.items ?? []);
    } catch (e) {
      setError(e?.message || 'Erreur chargement des sections');
      setSections([]);
    }
  };

  React.useEffect(() => {
    fetchSections();
    const t = setTimeout(() => setAllowEmpty(true), 250);
    return () => clearTimeout(t);
  }, []);

  const persistOrder = async (orderedItems) => {
    try {
      await Promise.all(
        orderedItems.map((rec, idx) => pb.collection('site_home_sections').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  const onDragStart = (e, index) => {
    dragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    setSections((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      dragIndexRef.current = overIndex;
      return next;
    });
  };

  const onDrop = async () => {
    dragIndexRef.current = -1;
    const ordered = [...sections];
    await persistOrder(ordered);
    await fetchSections();
  };

  const openCreate = () => {
    setEditing(null);
    setImageDragOver(false);
    setRemoveExistingImage(false);
    try {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    } catch {}
    setImagePreviewUrl('');
    setForm({
      ...emptyForm,
      sort_order: sections.length ? Math.max(...sections.map(s => Number(s.sort_order || 0))) + 1 : 0,
    });
    setOpen(true);
  };

  const openEdit = (section) => {
    setEditing(section);
    setImageDragOver(false);
    setRemoveExistingImage(false);
    try {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    } catch {}
    setImagePreviewUrl('');
    setForm({
      key: section?.key || '',
      title: section?.title || '',
      content: section?.content || '',
      is_active: section?.is_active !== false,
      sort_order: Number(section?.sort_order || 0),
      image: null,
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);

    setImageDragOver(false);
    setRemoveExistingImage(false);
    try {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    } catch {}
    setImagePreviewUrl('');
  };

  const setImageFile = (file) => {
    if (!(file instanceof File)) return;
    setRemoveExistingImage(false);
    setForm((f) => ({ ...f, image: file }));
    try {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    } catch {}
    try {
      const url = URL.createObjectURL(file);
      setImagePreviewUrl(url);
    } catch {
      setImagePreviewUrl('');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const data = new FormData();
      data.append('key', form.key || '');
      data.append('title', form.title || '');
      data.append('content', form.content || '');
      data.append('is_active', form.is_active ? 'true' : 'false');
      data.append('sort_order', String(Number(form.sort_order || 0)));
      if (removeExistingImage) data.append('image', '');
      if (form.image instanceof File) data.append('image', form.image);

      if (editing?.id) {
        await pb.collection('site_home_sections').update(editing.id, data);
      } else {
        await pb.collection('site_home_sections').create(data);
      }
      await fetchSections();
      closeModal();
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette section ?')) return;
    try {
      await pb.collection('site_home_sections').delete(id);
      await fetchSections();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (section) => {
    try {
      await pb.collection('site_home_sections').update(section.id, { is_active: !(section?.is_active !== false) });
      await fetchSections();
    } catch (e) {
      alert(e?.message || "Erreur de mise à jour");
    }
  };

  const move = async (id, dir) => {
    const ordered = [...sections].sort((a, b) => {
      const ao = Number(a.sort_order || 0);
      const bo = Number(b.sort_order || 0);
      if (ao !== bo) return ao - bo;
      return (a.created || '').localeCompare(b.created || '');
    });

    const idx = ordered.findIndex(s => s.id === id);
    if (idx < 0) return;

    const swapWith = dir === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= ordered.length) return;

    const a = ordered[idx];
    const b = ordered[swapWith];
    const ao = Number(a.sort_order || 0);
    const bo = Number(b.sort_order || 0);

    try {
      await Promise.all([
        pb.collection('site_home_sections').update(a.id, { sort_order: bo }),
        pb.collection('site_home_sections').update(b.id, { sort_order: ao }),
      ]);
      await fetchSections();
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  return (
    <AdminLayout title="">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ marginTop: 0 }}>Accueil · Sections</h1>
          <p style={{ marginTop: 6 }}>Gérez les blocs de contenu de la page d’accueil (ordre, actif/inactif, image).</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <button
            className="btn-primary"
            onClick={openCreate}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 10,
              background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
              border: '1px solid rgba(79,70,229,0.6)',
              boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
              color: 'white',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </span>
            <span>Nouvelle section</span>
          </button>
        </div>
      </div>

      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .header-pill{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:8px 10px; border-radius:10px; }
        .header-pill:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .toggle-pill{ cursor:pointer; border:1px solid var(--admin-border); background: var(--admin-panel); color: var(--admin-text); padding:6px 10px; border-radius:999px; font-size:.85rem; }
        .toggle-pill.on{ border-color: rgba(34,197,94,0.55); box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
        .toggle-pill.off{ opacity:.8; }
        .sections-grid{ display:grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 12px; }
        @media (max-width: 480px){ .sections-grid{ grid-template-columns: 1fr; } }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 820px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-body{ padding: 16px 20px; display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .modal-body .full{ grid-column: 1 / -1; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .modal-actions { display:flex; gap: 12px; justify-content:flex-end; padding: 16px 20px; border-top:1px solid var(--admin-border); }
        .field{ display:flex; flex-direction:column; gap: 6px; }
        .field label{ font-size: .9rem; color: var(--admin-muted); }
        .field input[type="text"], .field input[type="number"], .field textarea{
          background: var(--admin-panel-2);
          border:1px solid var(--admin-border);
          color: var(--admin-text);
          border-radius: 10px;
          padding: 10px 10px;
          outline:none;
        }
        .field textarea{ min-height: 160px; resize: vertical; }
        .field input:focus, .field textarea:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        .field small{ color: var(--admin-muted); }
        .btn{ cursor:pointer; border:1px solid var(--admin-border); background: transparent; color: var(--admin-text); padding: 10px 12px; border-radius: 10px; }
        .btn:hover{ background: rgba(255,255,255,0.06); }
        .admin-shell.admin-light .btn:hover{ background: rgba(2,6,23,0.06); }
        .btn-solid{ border-color: rgba(79,70,229,0.6); background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85)); color:#fff; }
        .btn-solid:disabled{ opacity: .6; cursor: not-allowed; }
        .btn-danger{ border-color: rgba(239,68,68,0.5); }
      `}</style>

      <style>{`
        .dropzone{
          border: 2px dashed var(--admin-border);
          border-radius: 12px;
          padding: 14px;
          background: var(--admin-panel-2);
          display:flex;
          gap: 12px;
          align-items: center;
          justify-content: space-between;
        }
        .dropzone.dragover{
          border-color: rgba(79,70,229,0.55);
          box-shadow: 0 0 0 3px rgba(79,70,229,0.16);
        }
        .dropzone-left{ display:flex; flex-direction:column; gap:4px; }
        .dropzone-title{ font-weight:700; }
        .dropzone-sub{ color: var(--admin-muted); font-size:.9rem; }
        .dropzone-preview{ width: 120px; height: 80px; border-radius: 10px; object-fit: cover; border:1px solid var(--admin-border); background: rgba(0,0,0,0.06); }
        .dropzone-thumb{ position: relative; width: 120px; height: 80px; }
        .dropzone-thumb .dropzone-preview{ width: 120px; height: 80px; }
        .dropzone-thumb-actions{ position: absolute; right: 6px; top: 6px; display:flex; gap: 6px; }
        .dropzone-thumb-actions button{ cursor:pointer; border:1px solid var(--admin-border); background: rgba(0,0,0,0.45); color:#fff; padding: 6px; border-radius: 10px; }
        .dropzone-thumb-actions button:hover{ background: rgba(0,0,0,0.60); }
      `}</style>

      {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}

      <div className="card" style={{ marginTop: 12 }}>
        <div className="sections-grid">
          {sections.map((s, idx) => {
              const img = fileUrl(s, 'image', '600x0');
              const active = s?.is_active !== false;
              return (
                <div
                  key={s.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, idx)}
                  onDragOver={(e) => onDragOver(e, idx)}
                  onDrop={(e) => {
                    e.preventDefault();
                    onDrop();
                  }}
                  title={`Glissez pour réordonner (position ${idx + 1})`}
                  style={{ border: '1px solid var(--admin-border)', borderRadius: 12, overflow: 'hidden', background: 'linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2))', cursor: 'grab' }}
                >
                  <div style={{ height: 140, background: 'rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {img ? (
                      <img src={img} alt={s.title || 'Section'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ color: 'var(--admin-muted)' }}>Aucune image</div>
                    )}
                  </div>

                  <div style={{ padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: '1.02rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.title || '—'}</div>
                        <div style={{ color: 'var(--admin-muted)', marginTop: 4, fontSize: '.9rem' }}>key: {s.key || '—'}</div>
                      </div>
                      <button
                        className={'toggle-pill ' + (active ? 'on' : 'off')}
                        onClick={() => handleToggleActive(s)}
                        title={active ? 'Désactiver' : 'Activer'}
                      >
                        {active ? 'Actif' : 'Inactif'}
                      </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      <button className="header-pill" onClick={() => move(s.id, 'up')} title="Monter">⬆</button>
                      <button className="header-pill" onClick={() => move(s.id, 'down')} title="Descendre">⬇</button>
                      <span style={{ color: 'var(--admin-muted)', fontSize: '.85rem' }}>Ordre: {idx + 1}</span>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        <button className="header-pill" title="Modifier" onClick={() => openEdit(s)}>✏️</button>
                        <button className="header-pill" title="Supprimer" onClick={() => handleDelete(s.id)}>🗑️</button>
                      </div>
                    </div>

                    {s.content && (
                      <div style={{ marginTop: 10, color: 'var(--admin-muted)', fontSize: '.9rem', lineHeight: 1.3, maxHeight: 60, overflow: 'hidden' }}>
                        {String(s.content).replace(/<[^>]*>/g, '').slice(0, 160)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

          {sections.length === 0 && allowEmpty && (
            <div style={{ gridColumn: '1 / -1', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220, width: '100%' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: .7, display: 'block' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <h3 style={{ margin: 0 }}>Aucune section</h3>
                <p style={{ margin: 0, color: 'var(--admin-muted)' }}>Commencez par ajouter votre premier bloc.</p>
                <button className="btn-primary" onClick={openCreate} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', borderRadius: 10,
                  background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
                  border: '1px solid rgba(79,70,229,0.6)',
                  boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                  color: 'white'
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </span>
                  <span>Nouvelle section</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => {
          if (e.target?.classList?.contains('modal-overlay')) closeModal();
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editing?.id ? 'Modifier la section' : 'Nouvelle section'}</h3>
              <button className="close-btn" onClick={closeModal} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="field full">
                <label>Key</label>
                <input type="text" value={form.key} onChange={(e) => setForm(f => ({ ...f, key: e.target.value }))} placeholder="ex: about, services..." />
                <small>Identifiant interne (unique). Utilisé pour placer la section sur le site.</small>
              </div>
              <div className="field">
                <label>Ordre (sort_order)</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))} />
                <small>Plus petit = affiché en premier</small>
              </div>
              <div className="field full">
                <label>Titre</label>
                <input type="text" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Contenu</label>
                <textarea value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Texte / HTML / contenu richtext" />
              </div>
              <div className="field">
                <label>Actif</label>
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              </div>
              <div className="field">
                <label>Image</label>
                <div
                  className={`dropzone ${imageDragOver ? 'dragover' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setImageDragOver(true); }}
                  onDragEnter={(e) => { e.preventDefault(); setImageDragOver(true); }}
                  onDragLeave={() => setImageDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setImageDragOver(false);
                    const file = e.dataTransfer?.files?.[0];
                    if (file && file.type?.startsWith('image/')) setImageFile(file);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      imageInputRef.current?.click();
                    }
                  }}
                  onClick={() => imageInputRef.current?.click()}
                >
                  <div className="dropzone-left">
                    <div className="dropzone-title">Glisser-déposer une image</div>
                    <div className="dropzone-sub">ou cliquer pour choisir un fichier</div>
                    {form.image instanceof File ? (
                      <div className="dropzone-sub">Sélection: {form.image.name}</div>
                    ) : null}
                    {removeExistingImage ? (
                      <div className="dropzone-sub" style={{ color: '#ef4444' }}>Image supprimée (en attente d’enregistrement)</div>
                    ) : null}
                  </div>

                  {(imagePreviewUrl || (editing?.id && fileUrl(editing, 'image', '600x0'))) && !removeExistingImage ? (
                    <div className="dropzone-thumb" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <img
                        className="dropzone-preview"
                        src={imagePreviewUrl || fileUrl(editing, 'image', '600x0')}
                        alt={imagePreviewUrl ? 'Aperçu' : 'Image actuelle'}
                      />
                      <div className="dropzone-thumb-actions">
                        <button
                          type="button"
                          title="Supprimer l’image"
                          aria-label="Supprimer l’image"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            setForm((f) => ({ ...f, image: null }));
                            try {
                              if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
                            } catch {}
                            setImagePreviewUrl('');
                            if (imageInputRef.current) imageInputRef.current.value = '';
                            if (editing?.id) setRemoveExistingImage(true);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setImageFile(file);
                  }}
                />
                <small>{editing?.id ? 'Laisser vide pour conserver l’image actuelle' : 'Choisir une image'}</small>
              </div>
            </div>
            <div className="modal-actions">
              {editing?.id && (
                <button className="btn btn-danger" onClick={() => { closeModal(); handleDelete(editing.id); }}>Supprimer</button>
              )}
              <button className="btn" onClick={closeModal}>Annuler</button>
              <button className="btn btn-solid" onClick={handleSave} disabled={loading}>
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSiteHomeSectionsPage;
